import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

// "My shift" — the rest of the employee to-do list (CJ 2026-08-09).
//
// Three sections CJ asked for, above the "before you go" checklist:
//   * board messages waiting to be read and acknowledged
//   * clock in / out reminders
//   * what is left on the task list this period
//
// Every row here is quiet unless it needs something. A panel that always shows
// four green ticks is a panel people stop reading, and then the one day it says
// something real they scroll past it. So: nothing to do renders nothing.
//
// Monarch's clock is retired — staff clock in Square. The clock row therefore
// tells someone to go and do it there; it never offers a button that looks like
// it clocks you in and doesn't.
export function MyShift({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [shift, setShift] = useState(null);
  const [mustRead, setMustRead] = useState(0);

  const load = useCallback(async () => {
    // Board must-reads come from the endpoint that already owns that rule,
    // rather than a second copy of the query that could disagree with it.
    const [s, b] = await Promise.all([
      api('GET', '/api/my-shift', null, headers).catch(() => null),
      api('GET', '/api/board/must-read-count', null, headers).catch(() => null),
    ]);
    if (s) setShift(s);
    setMustRead(Number(b && b.count) || 0);
  }, [currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const go = (tab) => window.dispatchEvent(new CustomEvent('monarch:navigate', { detail: tab }));

  const clock = shift && shift.clock;
  const tasks = shift && shift.tasks;
  const clockNeedsAction = !!(clock && clock.message);
  const tasksLeft = !!(tasks && tasks.remaining > 0 && tasks.period);

  // Quiet when there is nothing to say.
  if (!clockNeedsAction && mustRead === 0 && !tasksLeft) return null;

  const row = (key, tone, text, action, tab) => (
    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                            border: '1px solid #eee', borderRadius: 8, padding: '9px 11px',
                            borderLeft: `3px solid ${tone}` }}>
      <span style={{ flex: 1, minWidth: 160, fontSize: 13, color: '#212121' }}>{text}</span>
      {action && (
        <button onClick={() => go(tab)} className="btn-secondary" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
          {action}
        </button>
      )}
    </div>
  );

  return (
    <div className="card" style={{ borderLeft: '4px solid #b48a47' }}>
      <div className="text-base font-semibold">Your shift</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
        {clockNeedsAction && row(
          'clock', '#c62828', clock.message,
          // No button. Monarch cannot clock anyone in — sending them to a
          // screen that also can't would be worse than saying nothing.
          null, null,
        )}
        {mustRead > 0 && row(
          'board', '#8a6d1f',
          `${mustRead} board post${mustRead === 1 ? '' : 's'} to read and acknowledge`,
          'Open the Board', 'board',
        )}
        {tasksLeft && row(
          'tasks', '#b48a47',
          `${tasks.remaining} of ${tasks.total} ${tasks.period} task${tasks.total === 1 ? '' : 's'} still to do`,
          'See the list', 'today',
        )}
      </div>
    </div>
  );
}
